import type { LucideIcon } from "lucide-react";

type HealthStatus = "green" | "yellow" | "red";

interface MetricCardProps {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  delta?: {
    value: number;
    label: string;
  };
  /** When true, positive delta renders green. Default false (positive = red, for breach/error metrics). */
  positiveIsGood?: boolean;
  health?: HealthStatus;
  className?: string;
}

const healthColors: Record<HealthStatus, string> = {
  green: "bg-green-500",
  yellow: "bg-yellow-500",
  red: "bg-red-500",
};

const deltaColors = {
  positive: "text-green-600 dark:text-green-400",
  negative: "text-red-600 dark:text-red-400",
  neutral: "text-gray-500 dark:text-gray-400",
};

export function getDeltaDisplay(delta: { value: number; label: string }, positiveIsGood: boolean) {
  const upColor = positiveIsGood ? deltaColors.positive : deltaColors.negative;
  const downColor = positiveIsGood ? deltaColors.negative : deltaColors.positive;
  if (delta.value > 0) {
    return { arrow: "\u25B2", color: upColor, text: `${delta.value} ${delta.label}` };
  }
  if (delta.value < 0) {
    return { arrow: "\u25BC", color: downColor, text: `${Math.abs(delta.value)} ${delta.label}` };
  }
  return { arrow: "\u2014", color: deltaColors.neutral, text: delta.label };
}

export function MetricCard({ label, value, icon: Icon, delta, positiveIsGood = false, health, className = "" }: MetricCardProps) {
  return (
    <div className={`bg-white dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 p-4 text-center ${className}`}>
      {Icon && (
        <Icon className="h-4 w-4 text-gray-400 mx-auto mb-1" />
      )}
      <div className="flex items-center justify-center gap-2">
        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</span>
        {health && (
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${healthColors[health]}`} />
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</p>
      {delta && (() => {
        const d = getDeltaDisplay(delta, positiveIsGood);
        return (
          <p className={`text-xs mt-1 ${d.color}`}>
            {d.arrow} {d.text}
          </p>
        );
      })()}
    </div>
  );
}
